import React from "react";
import { Switch, Tag, Tooltip } from "antd";

type FinancialYear = {
    id: number;
    start_date: string;
    end_date: string;
    title?: string;
};

export default class CommonUtils {

    // -------------------------
    // STATUS TAG
    // -------------------------
    public getStatusNameByLevel(status: any = 0) {
        if (status === true || status === 1) {
            return <Tag color="success">Active</Tag>;
        }
        return <Tag color="default">Inactive</Tag>;
    }

    // -------------------------
    // SWITCH BUTTON
    // -------------------------
    public displaySwitchToggleBtn(
        entity: any,
        status: any,
        callback: (checked: boolean, entity: any) => void
    ) {
        return (
            <Switch
                size="small"
                checked={!!status}
                onChange={(checked) => callback(checked, entity)}
            />
        );
    }

    // -------------------------
    // NUMBER CONVERSION (SAFE)
    // -------------------------
    public ToLocalNumber(value: string | number, isBangla: boolean = false) {
        if (value === null || value === undefined || value === "") return "-";

        if (!isBangla) return value.toString();

        const bn = ["০", "১", "২", "৩", "৪", "৫", "৬", "৭", "৮", "৯"];
        const en = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

        const input = value.toString();
        let result = "";

        for (const char of input) {
            const index = en.indexOf(char);
            result += index !== -1 ? bn[index] : char;
        }

        return result;
    }

    public translateNumber(value: any, isBangla: boolean = false) {
        if (!isBangla) {
            return value;
        }

        const en = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        const bn = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];

        let result = String(value);

        en.forEach((digit, index) => {
            result = result.replace(new RegExp(digit, "g"), bn[index]);
        });

        return result;
    }
    // -------------------------
    // FORMAT VALUE (SAFE + CONSISTENT)
    // -------------------------
    public formatValue(
        value: any,
        precision: number = 4,
        isBangla: boolean = false
    ) {
        const num = Number(value);

        if (value === null || value === undefined || value === "" || isNaN(num)) {
            return "0";
        }

        const formatted = num > 0 ? num.toFixed(precision) : "0";

        return this.ToLocalNumber(formatted, isBangla);
    }

    // -------------------------
    // TWO DECIMAL FORMAT
    // -------------------------
    public formatNumberTwoDecimal(
        value: any,
        isBangla: boolean = true,
        precision: number = 2
    ) {
        const num = Number(value);

        if (value === null || value === undefined || value === "" || isNaN(num)) {
            return "-";
        }

        if (num > 0) {
            return this.ToLocalNumber(num.toFixed(precision), isBangla);
        }

        return "-";
    }

    // -------------------------
    // MAP COORDINATE FORMAT
    // -------------------------
    public formatCoordinate(value: any, precision: number = 6) {
        const num = Number(value);

        if (value === null || value === undefined || value === "" || isNaN(num)) {
            return "";
        }

        return String(Number(num.toFixed(precision)));
    }

    // -------------------------
    // DATE FORMAT (SAFE)
    // -------------------------
    public ToLocalDate(
        englishdate: string,
        isBangla: boolean = true,
        dateDivider: string = "-"
    ) {
        if (!englishdate) return "-";

        if (!isBangla) return englishdate;

        const parts = englishdate.toString().split(dateDivider);

        const converted = parts.map((part) => {
            if (!isNaN(Number(part))) {
                return this.ToLocalNumber(part, isBangla);
            }
            return this.ToLocalMonth(part);
        });

        return converted.join(dateDivider);
    }

    // -------------------------
    // MONTH TRANSLATION
    // -------------------------
    private ToLocalMonth(month: string): string {
        const monthMap: Record<string, string> = {
            Jan: "জানুয়ারি",
            Feb: "ফেব্রুয়ারি",
            Mar: "মার্চ",
            Apr: "এপ্রিল",
            May: "মে",
            Jun: "জুন",
            Jul: "জুলাই",
            Aug: "আগস্ট",
            Sep: "সেপ্টেম্বর",
            Oct: "অক্টোবর",
            Nov: "নভেম্বর",
            Dec: "ডিসেম্বর",
        };

        return monthMap[month] || month;
    }

    public monthListwithTotal = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December", "Total"

    ];

    public monthList = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"

    ];


    // -------------------------
    // YES/NO TAG
    // -------------------------
    public getYesNoLevelByFieldValue(status: any = 0) {
        if (status === true || status === 1) {
            return <Tag color="success">Yes</Tag>;
        }
        return <Tag color="default">No</Tag>;
    }

    // -------------------------
    // LOCATION RENDER
    // -------------------------
    public getLocation(record: any, lang: string, userOfficeLevel: string) {
        return (
            <>
                {!['Block', 'Thana', 'District', 'Region'].includes(userOfficeLevel) && (
                    <Tooltip title="Region">
                        <span>
                            {lang === 'en' ? record?.region_name_en : record?.region_name_bn}
                            {record?.region_name_en && ", "}
                        </span>
                    </Tooltip>
                )}

                {!['Block', 'Thana', 'District'].includes(userOfficeLevel) && (
                    <Tooltip title="District">
                        <span>
                            {lang === 'en' ? record?.district_name_en : record?.district_name_bn},{" "}
                        </span>
                    </Tooltip>
                )}

                {!['Block', 'Thana'].includes(userOfficeLevel) && (
                    <Tooltip title="Upazila">
                        <span>
                            {lang === 'en' ? record?.thana_name_en : record?.thana_name_bn}
                            {record?.union_name_en && ","}
                        </span>
                    </Tooltip>
                )}

                {userOfficeLevel !== 'Block' && (
                    <Tooltip title="Union">
                        <span>
                            {lang === 'en' ? record?.union_name_en : record?.union_name_bn}{" "}
                        </span>
                    </Tooltip>
                )}

                {record?.block_name_en && userOfficeLevel !== 'Block' && ", "}

                <Tooltip title="Block">
                    <span>
                        {lang === 'en' ? record?.block_name_en : record?.block_name_bn}
                    </span>
                </Tooltip>
            </>
        );
    }

    public varietyTypes = [
        { value: "Hybrid", name_en: "Hybrid", name_bn: "হাইব্রিড" },
        { value: "HYV", name_en: "HYV (High Yielding Variety)", name_bn: "উপশী উচ্চ ফলনশীল" },
        { value: "Local", name_en: "Local", name_bn: "স্থানীয়" },
    ];

    // -------------------------
    // STATUS TAG
    // -------------------------
    public getStatusTag(status: string, t: any) {
        switch (status) {
            case "DRAFT":
                return <Tag color="orange">{t("Drafted")}</Tag>;
            case "SUBMITTED":
                return <Tag color="blue">{t("Submitted")}</Tag>;
            case "VERIFIED":
                return <Tag color="green">{t("Verified")}</Tag>;
            default:
                return <Tag color="default">{t("Unknown")}</Tag>;
        }
    }

    // -------------------------
    // ROPA BONA
    // -------------------------
    public renderRopaBona(item: any) {
        if (!(item.is_ropa || item.is_bona)) return null;

        const parts: string[] = [];
        if (item.is_ropa == 1) parts.push("R");
        if (item.is_bona == 1) parts.push("B");

        return `(${parts.join(", ")})`;
    }

    // -------------------------
    // STYLES
    // -------------------------
    public Commonstyles = {
        th: {
            border: "1px solid #ccc",
            padding: "8px",
            background: "#f9f9f9",
            textAlign: "center" as const
        },
        td: {
            border: "1px solid #ccc",
            padding: "8px",
            textAlign: "center" as const
        },
        tdRight: {
            border: "1px solid #ccc",
            padding: "8px",
            textAlign: "right" as const
        }
    };

    // -------------------------
    // FINANCIAL YEAR
    // -------------------------
    public static getNextFinancialYear(
        financialYearList: FinancialYear[],
        currentFY?: FinancialYear
    ): FinancialYear | undefined {

        if (!financialYearList?.length || !currentFY) return undefined;

        const currentEnd = new Date(currentFY.end_date).getTime();

        return financialYearList
            .filter(fy => new Date(fy.start_date).getTime() > currentEnd)
            .sort((a, b) =>
                new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
            )[0];
    }


    // Only for preference list export render and listing hidden columns
    public getLocationForPreferenceOnly = (record: any, lang: string, userOfficeLevel: string) => {

        return (
            <>
                {
                    !['Block', 'Thana', 'District', 'Region'].includes(userOfficeLevel) && (
                        <Tooltip title={'Region'}>
                            <span> {lang === 'en' ? record?.region_name_en : record?.region_name_bn} {record?.region_name_en && ', '} </span>
                        </Tooltip>
                    )
                }
                {
                    !['Block', 'Thana', 'District'].includes(userOfficeLevel) && (
                        <Tooltip title={'District'}>
                            <span> {lang === 'en' ? record?.district_name_en : record?.district_name_bn}, </span>
                        </Tooltip>
                    )
                }
                {
                    !['Block'].includes(userOfficeLevel) && (
                        <Tooltip title={'Upazila'}>
                            <span> {lang === 'en' ? record?.thana_name_en : record?.thana_name_bn}{record?.union_name_en && ','} </span>
                        </Tooltip>
                    )
                }
                {
                    userOfficeLevel !== 'Block' && (
                        <Tooltip title={'Union'}>
                            <span>{lang === 'en' ? record?.union_name_en : record?.union_name_bn} </span>
                        </Tooltip>
                    )
                }
                {
                    record?.block_name_en && userOfficeLevel !== 'Block' && ', '
                }
                <Tooltip title={'Block'}>
                    <span>{lang === 'en' ? record?.block_name_en : record?.block_name_bn} </span>
                </Tooltip>

            </>
        );
    };

    // Stored data এর সাথে Preference Data merge করা হয়েছে এখানে।
    public matchingStoredDataAndPreferenceData = (storedVarietyList: any[], cropVarietyPreferenceList: any[]) => {
        // Get all stored crop_variety_id
        const storedIds = new Set(
            storedVarietyList.map(item => item.crop_variety_id)
        );

        // Add flag to preference data
        const updatedPreferenceList = cropVarietyPreferenceList.map(item => ({
            ...item,
            is_new_variety: !storedIds.has(item.crop_variety_id)
        }));

        const newVarietyList = [
            //From storedVarietyList - only keep if crop_variety_id exists in cropVarietyPreferenceList
            // ...storedVarietyList.filter((v) => preferenceIds.has(v.crop_variety_id)),
            ...storedVarietyList,
            //From cropVarietyPreferenceList - keep all
            ...updatedPreferenceList
        ];

        // Deduplicate by crop_variety_id (preference data takes priority)
        const seen = new Set<number>();
        const mergedVarietyList = newVarietyList.filter((v) => {
            if (seen.has(v.crop_variety_id)) return false;

            seen.add(v.crop_variety_id);
            return true;
        });

        // Sort varieties, where I've try make the object sort by crop_variety_type_id
        const sortedVarietyList = [...mergedVarietyList].sort(
            (a, b) => a.crop_variety_type_id - b.crop_variety_type_id
        );

        return sortedVarietyList;
    }

    // Helper function to calculate rowSpan for a given key at an index
    public getRowSpan = (data: any[], index: number, key: string) => {
        let span = 1;
        for (let i = index + 1; i < data.length; i++) {
            if (data[i][key] == null || data[index][key] == null) break;

            if (data[i][key] === data[index][key]) {
                span++;
            } else {
                break;
            }
        }
        return span;
    };

}
